import * as logClient from "@classdojo/log-client";
import { HeadlineText, Button, DisplayText } from "@classdojo/web/nessie";
import { RAW_cssValue } from "@classdojo/web/nessie/stylingLib";
import { translate } from "@classdojo/web/utils/translate";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ToolkitAppScroller from "components/BeyondToolkit/ToolkitAppScroller";
import { IdeasShuffleIcon } from "components/IdeasIcons";
import ToolkitCard from "components/ToolkitCard";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { randomIndex, generateLoadingAnimation } from "utils";
import { TABLE_TOPICS } from "utils/beyondToolkitTools";
import { sendUsage } from "utils/freemium";

const rolling = "/static/media/rolling-loop.mp3";
const finish = "/static/media/finish.mp3";

function TableTopics() {
  // this tells i18next to load these namespaces
  const { ready } = useTranslation(["dojo.beyond"]);
  const questions = useMemo(() => {
    return ready ? generateQuestions() : [];
  }, [ready]);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => randomIndex(questions));
  const [loading, setLoading] = useState(true);
  const [emojiIndex, setEmojiIndex] = useState(() => randomIndex(questions));
  const [showQuestion, setShowQuestion] = useState(true);

  const blinkQuestion = useRef<number | undefined>();
  const rollingAudioNode = useRef<HTMLAudioElement | null>(null);
  const finishAudioNode = useRef<HTMLAudioElement | null>(null);

  const newQuestion = () => {
    if (rollingAudioNode.current) {
      rollingAudioNode.current.play().catch(() => {
        logClient.sendMetrics([
          {
            type: "increment",
            value: 1,
            metricName: "toolkit.media.audio.play.error",
          },
        ]);
      });
    }
    setRandomQuestion();
    rerunAnimation();
    displayQuestion();
  };

  const updateEmoji = () => {
    setEmojiIndex(randomIndex(emojis));
  };

  const rerunAnimation = generateLoadingAnimation({
    numSegments: 6,
    endIntervalDuration: 100,
    startIntervalDuration: 300,
    onAnimationStateChange: setLoading,
    onAnimationTick: updateEmoji,
  });

  const setRandomQuestion = () => {
    setCurrentQuestionIndex(randomIndex(questions));
  };

  const displayQuestion = () => {
    setTimeout(() => {
      if (finishAudioNode.current) finishAudioNode.current.play();
      blinkQuestion.current = window.setInterval(() => {
        setShowQuestion((showQuestion) => !showQuestion);
      }, 200);
      window.setTimeout(() => {
        setShowQuestion(true);
        window.clearInterval(blinkQuestion.current);
        blinkQuestion.current = undefined;
      }, 800);
    }, 3000);
    // animation lasts for 3 seconds, so want to wait for that before displaying blinking text
  };

  // bulk ignore existing useEffects
  // eslint-disable-next-line @web-monorepo/no-useEffect
  useEffect(() => {
    if (ready) {
      newQuestion();
      sendUsage("dinnerTopics");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const { title, color, imgSrc } = TABLE_TOPICS();

  if (!ready) return null;

  return (
    <ToolkitAppScroller title={title} color={color} imgSrc={imgSrc}>
      <audio src={rolling} ref={rollingAudioNode} />
      <audio src={finish} ref={finishAudioNode} />
      <ToolkitCard>
        <div
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 1,
            position: "relative",
          }}
        >
          {loading ? (
            <DisplayText>{emojis[emojiIndex]}</DisplayText>
          ) : (
            showQuestion && (
              <HeadlineText as="h2" level={2}>
                {questions[currentQuestionIndex]}
              </HeadlineText>
            )
          )}
        </div>
        {loading ? (
          <Button data-name="index:table_topics:toolkit.table_topics.shuffling" kind="secondary" disabled>
            {translate("dojo.beyond:toolkit.table_topics.shuffling")}
          </Button>
        ) : (
          <button
            data-name="index:table_topics:ideas_shuffle_icon"
            sx={{
              color: "dt_content_accent",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "transparent",
              border: "none",
              outlineColor: "transparent",
            }}
            onClick={() => {
              logParentToolkitEvent({
                eventName: "table_topics.new_question.tap",
              });
              newQuestion();
            }}
          >
            <IdeasShuffleIcon color="dt_content_accent" sx={{ marginRight: RAW_cssValue("5px") }} />
            {translate("dojo.beyond:toolkit.table_topics.new_question")}
          </button>
        )}
      </ToolkitCard>
    </ToolkitAppScroller>
  );
}

export default TableTopics;

const generateQuestions = () => [
  translate("dojo.beyond:toolkit.table_topics.questions.describe_yourself"),
  translate("dojo.beyond:toolkit.table_topics.questions.good_at"),
  translate("dojo.beyond:toolkit.table_topics.questions.best_thing"),
  translate("dojo.beyond:toolkit.table_topics.questions.new_subject"),
  translate("dojo.beyond:toolkit.table_topics.questions.laugh_this_week"),
  translate("dojo.beyond:toolkit.table_topics.questions.superpower"),
  translate("dojo.beyond:toolkit.table_topics.questions.fifty_dollars"),
  translate("dojo.beyond:toolkit.table_topics.questions.be_animal"),
  translate("dojo.beyond:toolkit.table_topics.questions.like_to_learn"),
  translate("dojo.beyond:toolkit.table_topics.questions.learned_today"),
  translate("dojo.beyond:toolkit.table_topics.questions.design_playground"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_game"),
  translate("dojo.beyond:toolkit.table_topics.questions.nicest_person"),
  translate("dojo.beyond:toolkit.table_topics.questions.bedroom_one_thing"),
  translate("dojo.beyond:toolkit.table_topics.questions.desert_island"),
  translate("dojo.beyond:toolkit.table_topics.questions.vegetable_vs_homework"),
  translate("dojo.beyond:toolkit.table_topics.questions.go_again_this_week"),
  translate("dojo.beyond:toolkit.table_topics.questions.invisible_vs_flying"),
  translate("dojo.beyond:toolkit.table_topics.questions.one_holiday"),
  translate("dojo.beyond:toolkit.table_topics.questions.cheer_up"),
  translate("dojo.beyond:toolkit.table_topics.questions.cant_live_without"),
  translate("dojo.beyond:toolkit.table_topics.questions.anywhere_in_world"),
  translate("dojo.beyond:toolkit.table_topics.questions.spell_longest_word"),
  translate("dojo.beyond:toolkit.table_topics.questions.last_thank_you"),
  translate("dojo.beyond:toolkit.table_topics.questions.most_like_family"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_day"),
  translate("dojo.beyond:toolkit.table_topics.questions.sneeze_vs_hiccups"),
  translate("dojo.beyond:toolkit.table_topics.questions.wish_right_now"),
  translate("dojo.beyond:toolkit.table_topics.questions.pick_name"),
  translate("dojo.beyond:toolkit.table_topics.questions.last_high_five"),
  translate("dojo.beyond:toolkit.table_topics.questions.clothes_inside_out"),
  translate("dojo.beyond:toolkit.table_topics.questions.biggest_fear"),
  translate("dojo.beyond:toolkit.table_topics.questions.one_wish"),
  translate("dojo.beyond:toolkit.table_topics.questions.one_play_thing"),
  translate("dojo.beyond:toolkit.table_topics.questions.most_scary"),
  translate("dojo.beyond:toolkit.table_topics.questions.really_good_day"),
  translate("dojo.beyond:toolkit.table_topics.questions.recess"),
  translate("dojo.beyond:toolkit.table_topics.questions.great_at"),
  translate("dojo.beyond:toolkit.table_topics.questions.forever_age"),
  translate("dojo.beyond:toolkit.table_topics.questions.take_vs_be_in_picture"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_joke"),
  translate("dojo.beyond:toolkit.table_topics.questions.create_holiday"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_memory"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_book"),
  translate("dojo.beyond:toolkit.table_topics.questions.world_improvement"),
  translate("dojo.beyond:toolkit.table_topics.questions.funniest_face"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_thing"),
  translate("dojo.beyond:toolkit.table_topics.questions.perfect_dessert"),
  translate("dojo.beyond:toolkit.table_topics.questions.funniest_person"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_relative"),
  translate("dojo.beyond:toolkit.table_topics.questions.zoo_animal"),
  translate("dojo.beyond:toolkit.table_topics.questions.helped_parents"),
  translate("dojo.beyond:toolkit.table_topics.questions.good_friend"),
  translate("dojo.beyond:toolkit.table_topics.questions.silly_sound"),
  translate("dojo.beyond:toolkit.table_topics.questions.happiest_day"),
  translate("dojo.beyond:toolkit.table_topics.questions.change_world"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_season"),
  translate("dojo.beyond:toolkit.table_topics.questions.cartoon_character"),
  translate("dojo.beyond:toolkit.table_topics.questions.best_thing_age"),
  translate("dojo.beyond:toolkit.table_topics.questions.perfect_day"),
  translate("dojo.beyond:toolkit.table_topics.questions.best_friend"),
  translate("dojo.beyond:toolkit.table_topics.questions.grow_up"),
  translate("dojo.beyond:toolkit.table_topics.questions.do_differently_today"),
  translate("dojo.beyond:toolkit.table_topics.questions.best_family"),
  translate("dojo.beyond:toolkit.table_topics.questions.happy_today"),
  translate("dojo.beyond:toolkit.table_topics.questions.nice_deed"),
  translate("dojo.beyond:toolkit.table_topics.questions.any_pet"),
  translate("dojo.beyond:toolkit.table_topics.questions.famous_week"),
  translate("dojo.beyond:toolkit.table_topics.questions.family_motto"),
  translate("dojo.beyond:toolkit.table_topics.questions.make_movie"),
  translate("dojo.beyond:toolkit.table_topics.questions.keep_in_emergency"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_quality"),
  translate("dojo.beyond:toolkit.table_topics.questions.three_things"),
  translate("dojo.beyond:toolkit.table_topics.questions.outlaw_vegetable"),
  translate("dojo.beyond:toolkit.table_topics.questions.funny_vs_smart"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_month"),
  translate("dojo.beyond:toolkit.table_topics.questions.forever_dinner"),
  translate("dojo.beyond:toolkit.table_topics.questions.lunch_with"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_song"),
  translate("dojo.beyond:toolkit.table_topics.questions.older_vs_younger"),
  translate("dojo.beyond:toolkit.table_topics.questions.share_admiration"),
  translate("dojo.beyond:toolkit.table_topics.questions.feel_loved"),
  translate("dojo.beyond:toolkit.table_topics.questions.todays_favorite"),
  translate("dojo.beyond:toolkit.table_topics.questions.which_language"),
  translate("dojo.beyond:toolkit.table_topics.questions.school_best_worst"),
  translate("dojo.beyond:toolkit.table_topics.questions.todays_wish"),
  translate("dojo.beyond:toolkit.table_topics.questions.life_in_ten_years"),
  translate("dojo.beyond:toolkit.table_topics.questions.favorite_family_activity"),
  translate("dojo.beyond:toolkit.table_topics.questions.family_wish_month"),
  translate("dojo.beyond:toolkit.table_topics.questions.pick_hero"),
  translate("dojo.beyond:toolkit.table_topics.questions.scariest_memory"),
  translate("dojo.beyond:toolkit.table_topics.questions.stand_up"),
  translate("dojo.beyond:toolkit.table_topics.questions.children_over_adults"),
  translate("dojo.beyond:toolkit.table_topics.questions.describe_bravery"),
  translate("dojo.beyond:toolkit.table_topics.questions.dream_house"),
  translate("dojo.beyond:toolkit.table_topics.questions.looking_forward_to"),
  translate("dojo.beyond:toolkit.table_topics.questions.good_morning"),
  translate("dojo.beyond:toolkit.table_topics.questions.proud_of"),
  translate("dojo.beyond:toolkit.table_topics.questions.time_travel"),
  translate("dojo.beyond:toolkit.table_topics.questions.name_puppy"),
];

const emojis = [
  "🍓",
  "🎉",
  "👻",
  "✌️",
  "🐶",
  "🐱",
  "🐝",
  "🐳",
  "🌵",
  "🍄",
  "🍁",
  "🌼",
  "⭐️",
  "🔥",
  "🌎",
  "⛄️",
  "❄️",
  "🍎",
  "🍉",
  "🐙",
  "🐼",
  "🐯",
  "🍿",
  "⚽️",
  "🏀",
  "🎨",
  "🎹",
  "🥁",
  "🚀",
  "🏝",
  "💎",
  "🎁",
  "📓",
  "✏️",
];
